import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../ui/modal';
import { Button } from '../ui/button';
import { userApi } from '../../api/user.api';
import { ProfileImage } from '../../types/User.api.type';

interface ProfileImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentImageUrl?: string;
}

const ProfileImageModal = ({ isOpen, onClose, onSuccess, currentImageUrl }: ProfileImageModalProps) => {
  const [avatars, setAvatars] = useState<ProfileImage[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedUrl(null);
      setError('');
      fetchAvatars();
    }
  }, [isOpen]);

  const fetchAvatars = async () => {
    try {
      const res = await userApi.getProfileImages();
      setAvatars(res.data.results);
    } catch {
      setError('아바타 목록을 불러오지 못했어요');
    }
  };

  const handleSave = async () => {
    if (!selectedUrl || selectedUrl === currentImageUrl) return;

    setIsLoading(true);
    setError('');
    try {
      await userApi.updateProfileImage('avatar', selectedUrl);
      onSuccess();
      onClose();
    } catch {
      setError('변경에 실패했어요. 다시 시도해주세요');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentImageUrl) {
      onClose();
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await userApi.updateProfileImage('remove');
      onSuccess();
      onClose();
    } catch {
      setError('변경에 실패했어요. 다시 시도해주세요');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUrl(null);
    setError('');
    onClose();
  };

  const isChanged = selectedUrl !== null && selectedUrl !== currentImageUrl;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>
        <ModalTitle>프로필 이미지 선택</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="grid grid-cols-4 gap-3 mb-4">
          {avatars.map((avatar) => (
            <button
              key={avatar.id}
              type="button"
              onClick={() => setSelectedUrl(avatar.url)}
              className={`w-16 h-16 rounded-full overflow-hidden bg-white transition-transform ${
                selectedUrl === avatar.url
                  ? 'ring-2 ring-primary scale-105'
                  : 'hover:scale-105'
              }`}
            >
              <img
                src={avatar.url}
                alt={avatar.name}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={handleRemove} disabled={isLoading}>
            지우기
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={!isChanged || isLoading}>
            {isLoading ? '저장 중...' : '저장'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ProfileImageModal;
